import "./Controls.css"

import { forwardRef, useCallback, useEffect, useRef, useState } from "react"
import debounce from "lodash.debounce"

import {
  textValuesAreActuallyDifferent,
  getValidInputValue,
} from "web/components/Form/helpers"

import { formatValue } from "web/services/format"
import useCombinedRefs from "web/hooks/behaviour/useCombinedRefs"

import classNames from "classnames"

import ControlContainer from "./ControlContainer"

const Input = forwardRef((props, ref) => {
  const {
    name,
    value,
    formattedValue,
    format = null,
    kind,
    touched,
    inputType,
    error,
    onChange,
    onFocus,
    onBlur,
    onEnterKey,
    placeholder,
    autoFocus = false,
    autoSelect = false,
    rows = 1,
    disabled = false,
  } = props

  const innerRef = useRef()
  const inputRef = useCombinedRefs(ref, innerRef)

  const [hasFocus, setHasFocus] = useState(false)
  const [inputValue, setInputValue] = useState(getValidInputValue(value))

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const _handleChange = useCallback(
    debounce((value) => {
      const newValue = value === "" ? null : { value: value, kind, format }
      onChange(newValue)
    }, 200),
    []
  )

  useEffect(() => {
    if (autoFocus) inputRef.current.focus()
    if (autoSelect) inputRef.current.select()

    return () => {
      _handleChange.flush()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    const newInputValue = getValidInputValue(value)

    if (textValuesAreActuallyDifferent(newInputValue, inputValue)) {
      setInputValue(newInputValue)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value])

  function handleFocus(e) {
    setHasFocus(true)
    onFocus && onFocus(e)
  }

  function handleBlur(e) {
    _handleChange.flush()

    setHasFocus(false)
    onBlur && onBlur(e)
  }

  function handleChange(e) {
    _handleChange(e.target.value)
    setInputValue(e.target.value)
  }

  function handleKeyDown(e) {
    if (e.key === "Enter") {
      if (rows !== 1) return

      e.target.blur()
      onEnterKey && onEnterKey()
    }

    if (e.key === "Escape") {
      e.stopPropagation()
      e.target.blur()
    }
  }

  function getDefaultPlaceholder() {
    if (kind === "Number") {
      if (format === "Percent") return "%"
      return "#"
    }
    return null
  }

  const formattedInputValue =
    formattedValue ||
    formatValue({ value: inputValue, format, kind }) ||
    inputValue

  const inputProps = {
    "data-testid": `form-input-${name}`,
    ref: inputRef,
    className: classNames("form-control", { "opacity-50": disabled }),
    autoComplete: "off",
    value: hasFocus ? inputValue : formattedInputValue,
    type: inputType || "text",
    onChange: handleChange,
    onFocus: handleFocus,
    onBlur: handleBlur,
    onKeyDown: handleKeyDown,
    placeholder: placeholder || getDefaultPlaceholder(),
    disabled,
  }

  return (
    <ControlContainer
      {...props}
      errorCondition={
        error && (touched || (inputValue !== "" && error !== "Required."))
      }
    >
      {rows === 1 && <input {...inputProps} />}
      {rows !== 1 && <textarea rows={rows} {...inputProps}></textarea>}
    </ControlContainer>
  )
})

export default Input
